import {
  AppWindow,
  ArrowLeft,
  Check,
  ChevronRight,
  FileText,
  ListTodo,
  Route,
  Search,
  type LucideIcon,
  Star,
  UserRound,
} from "lucide-react";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ProcessSummary } from "../../api/types";
import { appIconUri } from "./appIcon";
import { buildDirectoryGroups } from "./directoryGrouping";
import { categoryGroup, categoryName, processCategoryId, useCategories } from "./processGroups";
import { persistFavouriteProcessSlugs, readFavouriteProcessSlugs } from "./processPreferences";
import { type SearchMatch, searchProcesses } from "./processSearch";

function MatchIcon({ match, groupIcon }: { match: SearchMatch; groupIcon: LucideIcon }) {
  const className = "h-3.5 w-3.5 shrink-0";
  if (match.field === "app") {
    const uri = appIconUri(match.value);
    if (uri) return <img src={uri} alt="" className={`${className} object-contain`} />;
  }
  const Icon =
    match.field === "app"
      ? AppWindow
      : match.field === "person"
        ? UserRound
        : match.field === "group"
          ? groupIcon
          : match.field === "name"
            ? Route
            : match.field === "step"
              ? ListTodo
              : FileText;
  return <Icon aria-hidden="true" className={className} />;
}

export default function ProcessSearchList({
  processes,
  people = {},
  recent = [],
  current,
  onSelect,
  browse = true,
}: {
  processes: ProcessSummary[];
  people?: Record<string, string>;
  recent?: string[];
  current?: string;
  onSelect: (process: ProcessSummary) => void;
  browse?: boolean;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState<string | null>(null);
  const categories = useCategories() ?? new Map();
  const domainId = (process: ProcessSummary) => processCategoryId(process, "business_domain");
  const domainName = (process: ProcessSummary) => categoryName(domainId(process), categories, t);
  const [favourites, setFavourites] = useState(readFavouriteProcessSlugs);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const recentRows = recent.flatMap(slug => processes.filter(process => process.slug === slug));
  const rows = searchProcesses(processes, query, people, favourites, recent, domainName);
  const browsing = browse && !query.trim() && !group;
  const shown = group && !query.trim() ? rows.filter(row => domainId(row.process) === group) : rows;
  const renderRows = (items: typeof rows) =>
    items.map(({ process, match }) => (
      <div key={process.slug} className="flex items-center gap-1 rounded-lg hover:bg-muted-surface">
        <button
          type="button"
          aria-label={t(
            favourites.includes(process.slug)
              ? "remove_process_from_favourites"
              : "add_process_to_favourites",
            { name: process.name },
          )}
          aria-pressed={favourites.includes(process.slug)}
          onClick={() => {
            const next = favourites.includes(process.slug)
              ? favourites.filter(slug => slug !== process.slug)
              : [...favourites, process.slug];
            persistFavouriteProcessSlugs(next);
            setFavourites(next);
          }}
          className="ml-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-text-dim focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Star
            aria-hidden="true"
            className={`h-3.5 w-3.5 ${favourites.includes(process.slug) ? "fill-info/70 text-info" : ""}`}
          />
        </button>
        <button
          type="button"
          data-process-select
          data-picker-option
          aria-current={current === process.slug ? "page" : undefined}
          onClick={() => onSelect(process)}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-lg py-2.5 pr-3 text-left focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-text">{process.name}</span>
            {match && (
              <span className="flex items-center gap-1.5 text-xs text-info" title={match.value}>
                <MatchIcon
                  match={match}
                  groupIcon={categoryGroup(domainId(process), categories).icon}
                />
                <span className="truncate">
                  {t(`processPage.match_${match.field}`, { value: match.value })}
                </span>
              </span>
            )}
            <span className="block truncate text-xs text-text-dim">
              {domainName(process)} ({t("n_instances", { count: process.frequency })})
            </span>
          </span>
          {current === process.slug && (
            <Check className="h-4 w-4 shrink-0 text-info" aria-hidden="true" />
          )}
        </button>
      </div>
    ));
  const heading = (label: string) => (
    <h2 className="px-3 pb-1 pt-3 text-xs font-semibold text-text-dim">{label}</h2>
  );
  return (
    <>
      <div className="relative shrink-0 border-b border-outline p-3">
        <Search
          className="pointer-events-none absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-dim"
          aria-hidden="true"
        />
        <input
          ref={input}
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          aria-label={t("processPage.searchProcesses")}
          placeholder={t("processPage.searchProcesses")}
          onKeyDown={event => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              list.current?.querySelector<HTMLButtonElement>("[data-process-select]")?.focus();
            }
          }}
          className="w-full rounded-lg border border-outline bg-surface py-2 pl-8 pr-2 text-sm text-text outline-none placeholder:text-text-dim focus:border-info focus:ring-2 focus:ring-info/20"
        />
      </div>
      <div
        ref={list}
        className="min-h-0 overflow-y-auto p-1.5"
        onKeyDown={event => {
          if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
          const buttons = [
            ...(list.current?.querySelectorAll<HTMLButtonElement>("[data-process-select]") ?? []),
          ];
          const index = buttons.indexOf(event.target as HTMLButtonElement);
          if (index < 0) return;
          event.preventDefault();
          const next = index + (event.key === "ArrowDown" ? 1 : -1);
          if (next < 0) input.current?.focus();
          else buttons[next]?.focus();
        }}
      >
        {browsing ? (
          <>
            {favourites.some(slug => processes.some(process => process.slug === slug)) && (
              <>
                {heading(t("favourites"))}
                {renderRows(rows.filter(row => favourites.includes(row.process.slug)).slice(0, 5))}
              </>
            )}
            {heading(t("processPage.recent"))}
            {recentRows.length ? (
              renderRows(recent.flatMap(slug => rows.filter(row => row.process.slug === slug)))
            ) : (
              <p className="px-3 py-2 text-xs text-text-dim">{t("processPage.recentEmpty")}</p>
            )}
            {heading(t("processPage.groups"))}
            {buildDirectoryGroups(
              processes,
              "domain",
              [],
              {},
              "total",
              1,
              new Set(),
              categories,
            ).map(({ id, icon: Icon, tone, processes: items }) => (
              <button
                key={id}
                type="button"
                onClick={() => setGroup(id)}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted-surface focus-visible:ring-2 focus-visible:ring-primary"
              >
                <span className={`rounded-md p-1.5 ${tone}`}>
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="flex-1">{categoryName(id, categories, t)}</span>
                <span className="text-xs text-text-dim">{items.length}</span>
                <ChevronRight className="h-4 w-4 text-text-dim" aria-hidden="true" />
              </button>
            ))}
          </>
        ) : (
          <>
            {group && !query.trim() && (
              <button
                type="button"
                onClick={() => setGroup(null)}
                className="flex items-center gap-2 p-2 text-sm text-text-mid"
              >
                <ArrowLeft size={14} />
                {t("processPage.back")}
              </button>
            )}
            <output className="px-3 py-2 text-xs text-text-dim">
              {t("processPage.results", { count: shown.length })}
            </output>
            {shown.length ? (
              renderRows(shown)
            ) : (
              <p className="px-3 py-4 text-sm text-text-dim">{t("no_results_match_filters")}</p>
            )}
          </>
        )}
      </div>
    </>
  );
}
